<template>
  <div>
    <Breadcrumbs title="Dashboard" />

    <div class="container-fluid">

      <!-- SKELETON STATE -->
      <template v-if="loading">

        <!-- Welcome strip -->
        <div class="mb-4">
          <div class="gl-skel mb-2" style="height:22px;width:220px;border-radius:6px"></div>
          <div class="gl-skel" style="height:13px;width:300px;border-radius:6px"></div>
        </div>

        <!-- Row 1: 4 KPI cards -->
        <div class="row g-3 mb-4">
          <div class="col-6 col-xl-3" v-for="i in 4" :key="i">
            <div class="card">
              <div class="card-body">
                <div class="gl-skel mb-3" style="width:38px;height:38px;border-radius:10px"></div>
                <div class="gl-skel mb-2" style="height:30px;width:90px;border-radius:6px"></div>
                <div class="gl-skel" style="height:11px;width:80px;border-radius:4px"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 2: Revenue Overview (8) + Recent Orders (4) -->
        <div class="row g-3 mb-4">
          <div class="col-xl-8">
            <div class="card h-100">
              <div class="card-header d-flex justify-content-between align-items-center">
                <div class="gl-skel" style="height:15px;width:140px;border-radius:5px"></div>
                <div class="gl-skel" style="height:12px;width:100px;border-radius:4px"></div>
              </div>
              <div class="card-body p-0">
                <div class="gl-skel" style="height:300px;border-radius:0 0 12px 12px"></div>
              </div>
            </div>
          </div>
          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header d-flex justify-content-between align-items-center">
                <div class="gl-skel" style="height:15px;width:110px;border-radius:5px"></div>
                <div class="gl-skel" style="height:12px;width:50px;border-radius:4px"></div>
              </div>
              <div class="card-body p-0">
                <div v-for="i in 7" :key="i" style="padding:12px 20px;border-bottom:1px solid #f3f4f6">
                  <div class="d-flex align-items-center gap-3">
                    <div class="gl-skel" style="width:8px;height:8px;border-radius:50%;flex-shrink:0"></div>
                    <div style="flex:1">
                      <div class="gl-skel mb-1" style="height:12px;width:70%;border-radius:4px"></div>
                      <div class="gl-skel" style="height:10px;width:40%;border-radius:4px"></div>
                    </div>
                    <div class="gl-skel" style="width:56px;height:12px;border-radius:4px"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 3: My Branches (4) + Orders by Status (4) + Quick Stats (4) -->
        <div class="row g-3 mb-4">
          <!-- My Branches: list of branch items -->
          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header d-flex justify-content-between align-items-center">
                <div class="gl-skel" style="height:15px;width:100px;border-radius:5px"></div>
                <div class="gl-skel" style="height:12px;width:50px;border-radius:4px"></div>
              </div>
              <div class="card-body">
                <div v-for="i in 5" :key="i" class="d-flex align-items-center gap-3 mb-3">
                  <div class="gl-skel" style="width:30px;height:30px;border-radius:8px;flex-shrink:0"></div>
                  <div style="flex:1">
                    <div class="gl-skel mb-1" style="height:12px;width:80%;border-radius:4px"></div>
                    <div class="gl-skel" style="height:10px;width:50%;border-radius:4px"></div>
                  </div>
                  <div class="gl-skel" style="width:52px;height:12px;border-radius:4px"></div>
                </div>
              </div>
            </div>
          </div>
          <!-- Orders by Status: donut chart -->
          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <div class="gl-skel" style="height:15px;width:120px;border-radius:5px"></div>
              </div>
              <div class="card-body d-flex flex-column align-items-center justify-content-center">
                <div class="gl-skel mb-4" style="width:200px;height:200px;border-radius:50%"></div>
                <div class="d-flex gap-3">
                  <div v-for="i in 3" :key="i" class="d-flex align-items-center gap-1">
                    <div class="gl-skel" style="width:10px;height:10px;border-radius:3px"></div>
                    <div class="gl-skel" style="width:48px;height:10px;border-radius:4px"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Quick Stats: stat rows + divider + alert list -->
          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <div class="gl-skel" style="height:15px;width:90px;border-radius:5px"></div>
              </div>
              <div class="card-body">
                <div v-for="i in 3" :key="i" class="d-flex align-items-center gap-3 mb-3 p-2">
                  <div class="gl-skel" style="width:36px;height:36px;border-radius:8px;flex-shrink:0"></div>
                  <div style="flex:1">
                    <div class="gl-skel" style="height:11px;width:80%;border-radius:4px"></div>
                  </div>
                  <div class="gl-skel" style="width:32px;height:16px;border-radius:4px"></div>
                </div>
                <div class="gl-skel mb-3" style="height:1px;width:100%"></div>
                <div v-for="i in 3" :key="'a'+i" class="d-flex align-items-center justify-content-between mb-2">
                  <div style="flex:1">
                    <div class="gl-skel mb-1" style="height:11px;width:75%;border-radius:4px"></div>
                    <div class="gl-skel" style="height:9px;width:50%;border-radius:4px"></div>
                  </div>
                  <div class="gl-skel" style="width:44px;height:20px;border-radius:6px"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 4: Branch Performance (6) + Daily Revenue Trend (6) -->
        <div class="row g-3">
          <div class="col-xl-6" v-for="i in 2" :key="i">
            <div class="card">
              <div class="card-header">
                <div class="gl-skel" style="height:15px;width:160px;border-radius:5px"></div>
              </div>
              <div class="card-body">
                <div class="gl-skel" style="height:280px;border-radius:8px"></div>
              </div>
            </div>
          </div>
        </div>

      </template>

      <!-- CONTENT -->
      <template v-else>

        <!-- Welcome Strip -->
        <div class="gl-dash-welcome mb-4">
          <div>
            <h4 class="gl-dash-welcome__heading">Good {{ timeOfDay }}, <span class="gl-dash-welcome__name">{{ ownerName }}</span></h4>
            <p class="gl-dash-welcome__sub">{{ todayDate }} &mdash; here's your business at a glance</p>
          </div>
        </div>

        <!-- KPI Cards -->
        <div class="row g-3 mb-4 gl-kpi-row">
          <div class="col-6 col-xl-3">
            <div class="card gl-kpi-card">
              <div class="card-body gl-kpi-body">
                <div class="gl-kpi-card__icon gl-kpi-card__icon--teal">
                  <vue-feather type="dollar-sign" size="19"></vue-feather>
                </div>
                <div class="gl-kpi-text">
                  <div class="gl-kpi-card__value">RM {{ formatShort(summary.total_revenue) }}</div>
                  <div class="gl-kpi-card__label">Total Revenue</div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6 col-xl-3">
            <div class="card gl-kpi-card">
              <div class="card-body gl-kpi-body">
                <div class="gl-kpi-card__icon gl-kpi-card__icon--blue">
                  <vue-feather type="shopping-cart" size="19"></vue-feather>
                </div>
                <div class="gl-kpi-text">
                  <div class="gl-kpi-card__value">{{ summary.total_orders }}</div>
                  <div class="gl-kpi-card__label">Total Orders</div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6 col-xl-3">
            <div class="card gl-kpi-card">
              <div class="card-body gl-kpi-body">
                <div class="gl-kpi-card__icon gl-kpi-card__icon--green">
                  <vue-feather type="home" size="19"></vue-feather>
                </div>
                <div class="gl-kpi-text">
                  <div class="gl-kpi-card__value">{{ summary.total_branches }}</div>
                  <div class="gl-kpi-card__label">Branches</div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6 col-xl-3">
            <div class="card gl-kpi-card">
              <div class="card-body gl-kpi-body">
                <div class="gl-kpi-card__icon gl-kpi-card__icon--amber">
                  <vue-feather type="users" size="19"></vue-feather>
                </div>
                <div class="gl-kpi-text">
                  <div class="gl-kpi-card__value">{{ summary.total_staff }}</div>
                  <div class="gl-kpi-card__label">Staff Members</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Revenue Chart + Recent Orders -->
        <div class="row g-3 mb-4">
          <div class="col-xl-8">
            <div class="card h-100">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">Revenue Overview</h5>
                  <span class="gl-dash-meta">Last 30 days &nbsp;·&nbsp; <strong>RM {{ formatNumber(summary.total_revenue) }}</strong></span>
                </div>
              </div>
              <div class="card-body p-0">
                <apexchart
                  v-if="revenueChartSeries.length && revenueChartSeries[0]?.data?.length"
                  type="area"
                  height="300"
                  :series="revenueChartSeries"
                  :options="revenueChartOptions"
                ></apexchart>
                <div v-else class="gl-dash-empty" style="height:300px">
                  <div class="gl-dash-empty__icon">
                    <vue-feather type="bar-chart-2" size="28"></vue-feather>
                  </div>
                  <p class="gl-dash-empty__title">No revenue data yet</p>
                  <p class="gl-dash-empty__sub">Revenue trend appears after your first completed order</p>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">Recent Orders</h5>
                  <router-link to="/owner/orders" class="gl-dash-link">View all</router-link>
                </div>
              </div>
              <div class="card-body p-0 gl-orders-feed">
                <div v-if="!recentOrders.length" class="gl-dash-empty">
                  <div class="gl-dash-empty__icon">
                    <vue-feather type="inbox" size="26"></vue-feather>
                  </div>
                  <p class="gl-dash-empty__sub">No orders yet</p>
                </div>
                <div v-for="order in recentOrders.slice(0, 7)" :key="order.id" class="gl-order-item">
                  <span class="gl-order-item__dot" :class="`gl-order-dot--${order.status}`"></span>
                  <div class="gl-order-item__body">
                    <div class="gl-order-item__top">
                      <span class="gl-order-item__num">{{ order.order_number }}</span>
                      <span class="gl-order-item__amount">RM {{ formatNumber(order.total) }}</span>
                    </div>
                    <div class="gl-order-item__bottom">
                      <span class="gl-order-item__outlet">{{ order.outlet_name }}</span>
                      <span :class="getStatusBadgeClass(order.status)">{{ order.status }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- My Branches + Orders by Status + Quick Stats -->
        <div class="row g-3 mb-4">
          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">My Branches</h5>
                  <router-link to="/owner/branches" class="gl-dash-link">View all</router-link>
                </div>
              </div>
              <div class="card-body">
                <div v-if="revenueByBranch.length" class="gl-branch-list">
                  <div v-for="branch in revenueByBranch.slice(0, 5)" :key="branch.outlet_id" class="gl-branch-item">
                    <div class="gl-branch-item__icon">
                      <vue-feather type="map-pin" size="15"></vue-feather>
                    </div>
                    <div class="gl-branch-item__info">
                      <span class="gl-branch-item__name">{{ branch.outlet_name }}</span>
                      <span class="gl-branch-item__meta">{{ branch.orders }} orders</span>
                    </div>
                    <span class="gl-branch-item__revenue">RM {{ formatNumber(branch.revenue) }}</span>
                  </div>
                </div>
                <div v-else-if="branches.length" class="gl-branch-list">
                  <div v-for="branch in branches.slice(0, 5)" :key="branch.id" class="gl-branch-item">
                    <div class="gl-branch-item__icon">
                      <vue-feather type="map-pin" size="15"></vue-feather>
                    </div>
                    <div class="gl-branch-item__info">
                      <span class="gl-branch-item__name">{{ branch.name }}</span>
                      <span class="gl-branch-item__meta">{{ branch.city || 'No city set' }}</span>
                    </div>
                    <span class="badge bg-light text-muted">No data</span>
                  </div>
                </div>
                <div v-else class="gl-dash-empty">
                  <div class="gl-dash-empty__icon">
                    <vue-feather type="map-pin" size="26"></vue-feather>
                  </div>
                  <p class="gl-dash-empty__sub">No branches yet</p>
                  <router-link to="/owner/branches/create" class="btn btn-primary btn-sm mt-2">Add Branch</router-link>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">Orders by Status</h5>
              </div>
              <div class="card-body">
                <apexchart
                  type="donut"
                  height="260"
                  :series="orderStatusSeries"
                  :options="orderStatusOptions"
                ></apexchart>
              </div>
            </div>
          </div>

          <div class="col-xl-4">
            <div class="card h-100">
              <div class="card-header">
                <h5 class="mb-0">Quick Stats</h5>
              </div>
              <div class="card-body">
                <div class="gl-quick-stats">
                  <router-link to="/owner/products" class="gl-quick-stat">
                    <div class="gl-quick-stat__icon" style="background:rgba(139,92,246,0.1);color:#7c3aed">
                      <vue-feather type="package" size="17"></vue-feather>
                    </div>
                    <span class="gl-quick-stat__label">Products</span>
                    <span class="gl-quick-stat__value">{{ summary.total_products }}</span>
                  </router-link>

                  <router-link to="/owner/transfers" class="gl-quick-stat">
                    <div class="gl-quick-stat__icon" :style="{ background: summary.pending_transfers > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(156,163,175,0.1)', color: summary.pending_transfers > 0 ? '#d97706' : '#9ca3af' }">
                      <vue-feather type="truck" size="17"></vue-feather>
                    </div>
                    <span class="gl-quick-stat__label">Pending Transfers</span>
                    <span class="gl-quick-stat__value" :class="summary.pending_transfers > 0 ? 'gl-stat-warn' : ''">{{ summary.pending_transfers }}</span>
                  </router-link>

                  <router-link to="/owner/stock" class="gl-quick-stat">
                    <div class="gl-quick-stat__icon" :style="{ background: summary.low_stock_count > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: summary.low_stock_count > 0 ? '#dc2626' : '#059669' }">
                      <vue-feather :type="summary.low_stock_count > 0 ? 'alert-triangle' : 'check-circle'" size="17"></vue-feather>
                    </div>
                    <span class="gl-quick-stat__label">Low Stock Items</span>
                    <span class="gl-quick-stat__value" :class="summary.low_stock_count > 0 ? 'gl-stat-danger' : 'gl-stat-ok'">{{ summary.low_stock_count }}</span>
                  </router-link>
                </div>

                <template v-if="lowStockAlerts.length > 0">
                  <div class="gl-divider my-3"></div>
                  <p class="gl-section-label text-danger mb-2">
                    <vue-feather type="alert-triangle" size="12" class="me-1"></vue-feather>Low Stock Alerts
                  </p>
                  <div v-for="alert in lowStockAlerts.slice(0, 4)" :key="`${alert.outlet_id}-${alert.product_id}`" class="gl-stock-alert">
                    <div class="gl-stock-alert__info">
                      <span class="gl-stock-alert__name">{{ alert.product_name }}</span>
                      <span class="gl-stock-alert__branch">{{ alert.outlet_name }}</span>
                    </div>
                    <span class="gl-stock-alert__badge" :class="alert.stock === 0 ? 'gl-stock-alert__badge--critical' : 'gl-stock-alert__badge--low'">
                      {{ alert.stock }} / {{ alert.min_stock }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Branch Performance + Daily Revenue -->
        <div class="row g-3 align-items-stretch">
          <div class="col-xl-6 d-flex">
            <div class="card w-100">
              <div class="card-header">
                <h5 class="mb-0">Branch Performance</h5>
              </div>
              <div class="card-body">
                <apexchart
                  type="bar"
                  height="280"
                  :series="branchPerformanceSeries"
                  :options="branchPerformanceOptions"
                ></apexchart>
              </div>
            </div>
          </div>
          <div class="col-xl-6 d-flex">
            <div class="card w-100">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">Daily Revenue Trend</h5>
                  <span class="gl-dash-meta">Last 7 days</span>
                </div>
              </div>
              <div class="card-body">
                <apexchart
                  type="bar"
                  height="280"
                  :series="dailyRevenueSeries"
                  :options="dailyRevenueOptions"
                ></apexchart>
              </div>
            </div>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import { ownerDashboardApi, ownerBranchApi } from '@/services/ownerApi';
import Breadcrumbs from '@/layout/breadCrumbs.vue';

const BRAND = '#900C27';
const BRAND2 = '#e05a5e';

export default {
  name: 'OwnerDashboard',
  components: { Breadcrumbs },

  data() {
    return {
      loading: true,
      summary: {
        total_revenue: 0,
        total_orders: 0,
        total_branches: 0,
        total_staff: 0,
        total_products: 0,
        pending_transfers: 0,
        low_stock_count: 0
      },
      ordersByStatus: {},
      revenueByBranch: [],
      branches: [],
      recentOrders: [],
      dailyRevenue: [],
      lowStockAlerts: [],

      revenueChartSeries: [],
      revenueChartOptions: {
        chart: { type: 'area', height: 300, toolbar: { show: false }, sparkline: { enabled: false } },
        colors: [BRAND],
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95, 100] }
        },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f3f4f6', strokeDashArray: 4, xaxis: { lines: { show: false } } },
        xaxis: { type: 'datetime', labels: { format: 'dd MMM', style: { fontSize: '11px', colors: '#9ca3af' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { formatter: (v) => 'RM ' + v.toFixed(0), style: { fontSize: '11px', colors: '#9ca3af' } } },
        tooltip: { x: { format: 'dd MMM yyyy' }, y: { formatter: (v) => 'RM ' + v.toFixed(2) } },
        legend: { show: false }
      },

      orderStatusSeries: [],
      orderStatusOptions: {
        chart: { type: 'donut', height: 260 },
        colors: ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#06b6d4'],
        labels: [],
        legend: { position: 'bottom', fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif' },
        plotOptions: {
          pie: { donut: { size: '62%', labels: { show: true, total: { show: true, label: 'Total', fontFamily: 'Plus Jakarta Sans, sans-serif', formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0) } } } }
        },
        dataLabels: { enabled: false }
      },

      branchPerformanceSeries: [],
      branchPerformanceOptions: {
        chart: { type: 'bar', height: 260, toolbar: { show: false } },
        colors: [BRAND],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '55%' } },
        dataLabels: { enabled: true, formatter: (val) => 'RM ' + val.toFixed(0), style: { fontSize: '11px', fontFamily: 'Plus Jakarta Sans, sans-serif' } },
        grid: { borderColor: '#f3f4f6' },
        xaxis: { categories: [], labels: { formatter: (val) => 'RM ' + val, style: { fontSize: '11px', colors: '#9ca3af' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        tooltip: { y: { formatter: (val) => 'RM ' + val.toFixed(2) } }
      },

      dailyRevenueSeries: [],
      dailyRevenueOptions: {
        chart: { type: 'bar', height: 280, toolbar: { show: false } },
        colors: [BRAND, '#e5e7eb'],
        plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f3f4f6', strokeDashArray: 4 },
        xaxis: { categories: [], labels: { rotate: -40, style: { fontSize: '11px', colors: '#9ca3af' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { formatter: (val) => 'RM ' + val, style: { fontSize: '11px', colors: '#9ca3af' } } },
        legend: { position: 'top', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '12px' },
        tooltip: { y: { formatter: (val) => 'RM ' + val.toFixed(2) } }
      }
    };
  },

  computed: {
    timeOfDay() {
      const h = new Date().getHours();
      if (h < 12) return 'morning';
      if (h < 17) return 'afternoon';
      return 'evening';
    },
    ownerName() {
      try {
        const user = JSON.parse(localStorage.getItem('getlokal_admin_user') || '{}');
        return user.name?.split(' ')[0] || 'there';
      } catch { return 'there'; }
    },
    todayDate() {
      return new Date().toLocaleDateString('en-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
  },

  mounted() {
    Promise.all([this.fetchDashboard(), this.fetchBranches()]);
  },

  methods: {
    async fetchDashboard() {
      try {
        this.loading = true;
        const response = await ownerDashboardApi.getDashboard();
        const data = response.data.data;
        this.summary = data.summary;
        this.ordersByStatus = data.orders_by_status;
        this.revenueByBranch = data.revenue_by_branch;
        this.recentOrders = data.recent_orders;
        this.dailyRevenue = data.daily_revenue;
        this.lowStockAlerts = data.low_stock_alerts || [];
        this.updateCharts();
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBranches() {
      try {
        const response = await ownerBranchApi.list({ per_page: 20 });
        this.branches = response.data.data || [];
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    },

    updateCharts() {
      if (this.dailyRevenue.length) {
        this.revenueChartSeries = [{ name: 'Revenue', data: this.dailyRevenue.map(d => ({ x: new Date(d.date).getTime(), y: parseFloat(d.revenue) || 0 })) }];
      }

      const statusLabels = Object.keys(this.ordersByStatus);
      if (statusLabels.length) {
        this.orderStatusSeries = Object.values(this.ordersByStatus).map(v => parseInt(v));
        this.orderStatusOptions = { ...this.orderStatusOptions, labels: statusLabels.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')) };
      }

      if (this.revenueByBranch.length) {
        this.branchPerformanceSeries = [{ name: 'Revenue', data: this.revenueByBranch.map(b => parseFloat(b.revenue) || 0) }];
        this.branchPerformanceOptions = { ...this.branchPerformanceOptions, xaxis: { ...this.branchPerformanceOptions.xaxis, categories: this.revenueByBranch.map(b => b.outlet_name) } };
      }

      if (this.dailyRevenue.length) {
        this.dailyRevenueSeries = [
          { name: 'Revenue', data: this.dailyRevenue.map(d => parseFloat(d.revenue) || 0) },
          { name: 'Orders', data: this.dailyRevenue.map(d => parseInt(d.orders) || 0) }
        ];
        this.dailyRevenueOptions = { ...this.dailyRevenueOptions, xaxis: { ...this.dailyRevenueOptions.xaxis, categories: this.dailyRevenue.map(d => new Date(d.date).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' })) } };
      }
    },

    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatShort(value) {
      if (!value) return '0';
      const num = parseFloat(value);
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toFixed(2);
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },

    getStatusBadgeClass(status) {
      const classes = {
        pending: 'badge bg-warning',
        confirmed: 'badge bg-info',
        preparing: 'badge bg-primary',
        ready: 'badge bg-success',
        completed: 'badge bg-success',
        cancelled: 'badge bg-danger',
        delivered: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    }
  }
};
</script>

<style scoped>
/* ── Mobile responsive ───────────────────────────── */
@media (max-width: 767px) {
  /* KPI row: CSS grid forces all 4 cards to equal height */
  .gl-kpi-row {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 1fr;
    gap: 12px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .gl-kpi-row > [class*="col-"] {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
    flex: none !important;
  }
  .gl-kpi-row .gl-kpi-card {
    height: 100% !important;
    margin-bottom: 0 !important;
  }

  /* KPI card: icon left, text right */
  .gl-kpi-body {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 14px 14px !important;
  }
  .gl-kpi-card__icon {
    width: 36px !important;
    height: 36px !important;
    border-radius: 9px !important;
    margin-bottom: 0 !important;
    flex-shrink: 0 !important;
  }
  .gl-kpi-text { flex: 1; min-width: 0; }
  .gl-kpi-card__value {
    font-size: 1.15rem !important;
    margin-bottom: 2px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .gl-kpi-card__label { font-size: 0.68rem !important; }

  /* Hide heavy charts on mobile, show simple feed instead */
  .gl-mobile-hide { display: none !important; }

  /* Welcome strip */
  .gl-dash-welcome__heading { font-size: 1.1rem !important; }
  .gl-dash-welcome__sub { font-size: 0.78rem !important; }

  /* Full-width charts */
  .col-xl-8, .col-xl-4, .col-xl-6 { width: 100% !important; max-width: 100% !important; }
}

/* Desktop: stacked layout (default) */
.gl-kpi-body { display: block; }
.gl-kpi-text {}

/* Welcome */
.gl-dash-welcome {
  padding: 2px 0 8px;
}
.gl-dash-welcome__heading {
  font-size: 1.3rem;
  font-weight: 700;
  color: #111318;
  margin-bottom: 3px;
}
.gl-dash-welcome__name {
  color: #900C27;
}
.gl-dash-welcome__sub {
  font-size: 0.84rem;
  color: #9ca3af;
  margin: 0;
}
.gl-dash-meta {
  font-size: 0.8rem;
  color: #9ca3af;
}
.gl-dash-meta strong {
  color: #374151;
}
.gl-dash-link {
  font-size: 0.78rem;
  font-weight: 600;
  color: #900C27;
  text-decoration: none;
  letter-spacing: 0.01em;
}
.gl-dash-link:hover { color: #6f0a1e; }

/* KPI Cards */
.gl-kpi-card .card-body {
  padding: 20px;
}
.gl-kpi-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.gl-kpi-card__icon--teal   { background: rgba(144,12,39,0.1);  color: #900C27; }
.gl-kpi-card__icon--blue  { background: rgba(59,130,246,0.1); color: #3b82f6; }
.gl-kpi-card__icon--green { background: rgba(16,185,129,0.1); color: #059669; }
.gl-kpi-card__icon--amber { background: rgba(245,158,11,0.1); color: #d97706; }
.gl-kpi-card__value {
  font-size: 1.7rem;
  font-weight: 700;
  color: #111318;
  line-height: 1;
  margin-bottom: 5px;
  letter-spacing: -0.02em;
}
.gl-kpi-card__label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* Orders Feed */
.gl-orders-feed {
  overflow-y: auto;
  max-height: 348px;
}
.gl-order-item {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 11px 20px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s;
}
.gl-order-item:last-child { border-bottom: none; }
.gl-order-item:hover { background: #fafafa; }
.gl-order-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
}
.gl-order-dot--pending    { background: #f59e0b; }
.gl-order-dot--confirmed  { background: #3b82f6; }
.gl-order-dot--preparing  { background: #8b5cf6; }
.gl-order-dot--ready      { background: #10b981; }
.gl-order-dot--completed  { background: #10b981; }
.gl-order-dot--cancelled  { background: #ef4444; }
.gl-order-dot--delivered  { background: #10b981; }
.gl-order-item__body { flex: 1; min-width: 0; }
.gl-order-item__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
.gl-order-item__num {
  font-size: 0.78rem;
  font-weight: 600;
  color: #900C27;
  font-variant-numeric: tabular-nums;
}
.gl-order-item__amount {
  font-size: 0.84rem;
  font-weight: 700;
  color: #111318;
}
.gl-order-item__bottom {
  display: flex;
  align-items: center;
  gap: 5px;
}
.gl-order-item__outlet {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* Branches */
.gl-branch-list { display: flex; flex-direction: column; gap: 6px; }
.gl-branch-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 8px;
  background: #f9fafb;
  transition: background 0.15s;
}
.gl-branch-item:hover { background: #f0f2f5; }
.gl-branch-item__icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: rgba(144,12,39,0.08);
  color: #900C27;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gl-branch-item__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.gl-branch-item__name {
  font-size: 0.83rem;
  font-weight: 600;
  color: #111318;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gl-branch-item__meta { font-size: 0.73rem; color: #9ca3af; }
.gl-branch-item__revenue { font-size: 0.8rem; font-weight: 700; color: #059669; white-space: nowrap; }

/* Quick Stats */
.gl-quick-stats { display: flex; flex-direction: column; gap: 6px; }
.gl-quick-stat {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 11px;
  border-radius: 8px;
  background: #f9fafb;
  text-decoration: none;
  transition: background 0.15s;
}
.gl-quick-stat:hover { background: #f0f2f5; }
.gl-quick-stat__icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gl-quick-stat__label {
  flex: 1;
  font-size: 0.83rem;
  color: #6b7280;
}
.gl-quick-stat__value {
  font-size: 1rem;
  font-weight: 700;
  color: #111318;
}
.gl-stat-warn  { color: #d97706 !important; }
.gl-stat-danger { color: #dc2626 !important; }
.gl-stat-ok    { color: #059669 !important; }

/* Divider */
.gl-divider { height: 1px; background: #f3f4f6; }
.gl-section-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; display: flex; align-items: center; }

/* Stock Alerts */
.gl-stock-alert {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f9fafb;
}
.gl-stock-alert:last-child { border-bottom: none; }
.gl-stock-alert__info { display: flex; flex-direction: column; }
.gl-stock-alert__name { font-size: 0.8rem; font-weight: 500; color: #374151; }
.gl-stock-alert__branch { font-size: 0.72rem; color: #9ca3af; }
.gl-stock-alert__badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
}
.gl-stock-alert__badge--critical { background: rgba(239,68,68,0.1); color: #dc2626; }
.gl-stock-alert__badge--low      { background: rgba(245,158,11,0.1); color: #d97706; }

/* Empty state */
.gl-dash-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}
.gl-dash-empty__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.gl-dash-empty__title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.gl-dash-empty__sub {
  font-size: 0.8rem;
  color: #9ca3af;
  margin: 0;
  max-width: 200px;
}

/* ── Dark mode overrides ── */
body.dark-only .gl-order-item {
  border-bottom-color: rgba(255,255,255,0.06);
}
body.dark-only .gl-order-item:hover { background: rgba(255,255,255,0.04); }
body.dark-only .gl-order-item__amount { color: rgba(255,255,255,0.87); }

body.dark-only .gl-branch-item {
  background: rgba(255,255,255,0.05);
}
body.dark-only .gl-branch-item:hover { background: rgba(255,255,255,0.09); }
body.dark-only .gl-branch-item__name { color: rgba(255,255,255,0.87); }
body.dark-only .gl-branch-item__icon {
  background: rgba(144,12,39,0.20);
}

body.dark-only .gl-quick-stat {
  background: rgba(255,255,255,0.05);
}
body.dark-only .gl-quick-stat:hover { background: rgba(255,255,255,0.09); }
body.dark-only .gl-quick-stat__label { color: rgba(255,255,255,0.55); }
body.dark-only .gl-quick-stat__value { color: rgba(255,255,255,0.87); }

body.dark-only .gl-divider { background: rgba(255,255,255,0.06); }

body.dark-only .gl-stock-alert { border-bottom-color: rgba(255,255,255,0.06); }
body.dark-only .gl-stock-alert__name { color: rgba(255,255,255,0.80); }

body.dark-only .gl-dash-empty__icon {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.25);
}
body.dark-only .gl-dash-empty__title { color: rgba(255,255,255,0.70); }

body.dark-only .gl-dash-meta { color: rgba(255,255,255,0.40); }
body.dark-only .gl-dash-welcome__sub { color: rgba(255,255,255,0.45); }
body.dark-only .gl-kpi-card__label { color: rgba(255,255,255,0.50); }
body.dark-only .gl-kpi-card__value { color: rgba(255,255,255,0.90); }
</style>
